import { Controller } from "stimulus";
import Rails from "@rails/ujs";
import debounce from "lodash/debounce";

export default class extends Controller {
  static values = { url: String, dueDate: String };
  static targets = ["datePickerInput", "numberInput"];

  connect() {
    if (this.hasNumberInputTarget && this.hasDatePickerInputTarget) {
      this._boundSyncCalendarFromNumber = this._syncCalendarFromNumber.bind(this);
      this._boundSyncNumberFromCalendar = this._syncNumberFromCalendar.bind(this);

      this.numberInputTarget.addEventListener('blur', this._boundSyncCalendarFromNumber);
      this.datePickerInputTarget.addEventListener('change', this._boundSyncNumberFromCalendar);
    }

    if (this.hasNumberInputTarget) {
      this._boundDebouncedSave = debounce(this._saveIfChanged.bind(this), 1000);
      this.numberInputTarget.addEventListener('change', this._boundDebouncedSave);
    }

    if (this.hasDatePickerInputTarget) {
      this._boundSaveIfChanged = this._saveIfChanged.bind(this);
      this.datePickerInputTarget.addEventListener('change', this._boundSaveIfChanged);
    }

    this._lastSavedDays = this.hasNumberInputTarget ? this.numberInputTarget.value : "";
  }

  disconnect() {
    if (this.hasNumberInputTarget && this._boundSyncCalendarFromNumber) {
      this.numberInputTarget.removeEventListener('blur', this._boundSyncCalendarFromNumber);
    }

    if (this.hasDatePickerInputTarget && this._boundSyncNumberFromCalendar) {
      this.datePickerInputTarget.removeEventListener('change', this._boundSyncNumberFromCalendar);
    }

    if (this.hasNumberInputTarget && this._boundDebouncedSave) {
      this._boundDebouncedSave.cancel();
      this.numberInputTarget.removeEventListener('change', this._boundDebouncedSave);
    }

    if (this.hasDatePickerInputTarget && this._boundSaveIfChanged) {
      this.datePickerInputTarget.removeEventListener('change', this._boundSaveIfChanged);
    }
  }

  _saveIfChanged() {
    const currentDays = this.hasNumberInputTarget ? this.numberInputTarget.value : "";

    if (currentDays === this._lastSavedDays) return;

    this._lastSavedDays = currentDays;
    this.saveReminder();
  }

  saveReminder() {
    const form = new FormData();
    const days = this.hasNumberInputTarget ? this.numberInputTarget.value : "";

    if (days !== "" && days !== undefined) {
      form.append("task_reminder[days_before_due_date]", days);
    }

    this._lastSavedDays = days;

    Rails.ajax({
      url: this.urlValue,
      type: "PATCH",
      data: form,
      dataType: "script",
      success: (res) => {}
    });
  }

  clearReminder() {
    if (this.hasDatePickerInputTarget) {
      this.datePickerInputTarget.dispatchEvent(
        new CustomEvent("promote:datepicker-change", { detail: { value: "" } })
      );
    }

    if (this.hasNumberInputTarget) {
      this.numberInputTarget.value = "";
    }

    this.saveReminder();
  }

  _syncCalendarFromNumber(event) {
    const daysBefore = parseInt(event.target.value);
    if (isNaN(daysBefore) || daysBefore < 1 || !this.dueDateValue) {
      return;
    }

    const dueDate = new Date(this.dueDateValue);
    const reminderDate = new Date(dueDate);
    reminderDate.setUTCDate(dueDate.getUTCDate() - daysBefore);

    if (this.hasDatePickerInputTarget) {
      this.datePickerInputTarget.dispatchEvent(
        new CustomEvent("promote:datepicker-change", {
          detail: { value: this._formatDate(reminderDate) }
        })
      );
    }
  }

  _syncNumberFromCalendar() {
    if (!this.dueDateValue || !this.hasNumberInputTarget) return;

    const selectedValue = this.datePickerInputTarget.value;
    if (!selectedValue) return;

    const dueDate = new Date(this.dueDateValue);
    const selectedDate = new Date(selectedValue);
    const oneDay = 1000 * 3600 * 24; // milliseconds in one day
    const diffDays = (dueDate.getTime() - selectedDate.getTime()) / oneDay;

    if (diffDays >= 1) {
      this.numberInputTarget.value = diffDays;
    }
  }

  _formatDate(date) {
    return date.toISOString().slice(0, 10);
  }
}
